import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useLang } from "@/i18n/LanguageProvider";
import { waUrl, BOOK_MESSAGE } from "@/lib/contact";

export default function Header() {
  const { t, lang, toggle } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/rooms", label: t.nav.rooms },
    { to: "/restaurant", label: t.nav.restaurant },
    { to: "/laundry", label: t.nav.laundry },
    { to: "/travel", label: t.nav.travel },
    { to: "/faq", label: t.nav.faq },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "header-glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container-px mx-auto flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_agung-wiwin-bali/artifacts/596tdaku_3.jpg"
            alt="Agung Wiwin Homestay"
            className="w-12 h-12 md:w-14 md:h-14 object-contain rounded-full bg-white p-1 shadow-sm"
          />
          <div className="leading-tight">
            <div className="font-display text-lg md:text-xl text-ink">Agung Wiwin</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-cocoa">Homestay · Pererenan</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `relative text-sm tracking-wide transition-colors ${
                  isActive ? "text-moss" : "text-ink/80 hover:text-moss"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="hidden sm:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-ink/80 hover:text-moss"
          >
            <Globe className="w-4 h-4" />
            {lang.toUpperCase()}
          </button>
          <a
            href={waUrl(BOOK_MESSAGE())}
            target="_blank"
            rel="noreferrer"
            data-testid="header-book-cta"
            className="hidden md:inline-flex items-center rounded-full bg-moss px-5 py-2.5 text-sm text-white hover:bg-mossLight transition-colors shadow-sm hover:shadow-md"
          >
            {t.nav.book}
          </a>
          <button
            className="lg:hidden p-2 rounded-full hover:bg-sandDeep"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden header-glass border-t border-border mt-3" data-testid="mobile-menu">
          <div className="container-px py-6 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `text-base ${isActive ? "text-moss" : "text-ink/80"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <button
              onClick={toggle}
              className="text-xs uppercase tracking-[0.2em] text-ink/80 self-start inline-flex items-center gap-1.5"
              data-testid="mobile-lang-toggle"
            >
              <Globe className="w-4 h-4" /> {lang.toUpperCase()}
            </button>
            <a
              href={waUrl(BOOK_MESSAGE())}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center rounded-full bg-moss px-5 py-3 text-sm text-white"
            >
              {t.nav.book}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
